import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Image,
  Skeleton,
  SkeletonText,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { FaPlus } from 'react-icons/all';
import { useHistory } from 'react-router';
import { Heading } from '~/components';
import { Card } from '~/components/Card';
import logo from '~/assets/logo_technical_support.svg';
import { SearchSeriesNumber } from '~/pages/Portal/TechnicalSupport/SearchSeriesNumber';
import { EvaluateService } from '~/pages/Portal/TechnicalSupport/EvaluateService';
import { StepButtons } from '~/pages/Portal/TechnicalSupport/StepButtons';
import { AnswerOptions } from '~/pages/Portal/TechnicalSupport/AnswerOptions';
import { SelfService } from '~/types/technicalSupport';
import { EvaluateServiceSent } from '~/pages/Portal/TechnicalSupport/EvaluateServiceSent';
import { ROUTER_PATHS } from '~/constants';
import {
  newService,
  evaluateService,
  service,
} from '~/services/TechnicalSupport';

export interface NextStep {
  isBuscarNumeroSerie: boolean;
  isNaoEncontrado: boolean;
  isOpcoesSuporteTecnico: boolean;
  isAvaliacao: boolean;
  isAvaliacaoEnviada: boolean;
  isFinalizarAtendimento: boolean;
  isCorrecting?: boolean;
}

export const TechnicalSupport: React.VFC = () => {
  const initialStep: NextStep = {
    isBuscarNumeroSerie: true,
    isNaoEncontrado: false,
    isOpcoesSuporteTecnico: false,
    isAvaliacao: false,
    isAvaliacaoEnviada: false,
    isFinalizarAtendimento: false,
    isCorrecting: false,
  };
  const [step, setStep] = React.useState<NextStep>(initialStep);
  const [response, setResponse] = useState<SelfService>();
  const [message, setMessage] = useState<string>('');
  const [evaluate, setEvaluate] = useState<number>(0); // [0, 10]
  const [uuid, setUuid] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const toast = useToast();
  const history = useHistory();

  useEffect(() => {
    newService()
      .then(r => {
        setUuid(r.uuid);
        setResponse(r);
      })
      .catch(e => {
        toast({
          title: 'Erro',
          description: e.response.data.detail,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        history.goBack();
      });
  }, []);

  const header = () => {
    return (
      <HStack gridGap={5} p={3}>
        <Image src={logo} width="80px" />
        {step.isBuscarNumeroSerie && (
          <Stack>
            <Heading fontSize="20px" fontWeight="600">
              Precisa de ajuda?
            </Heading>
            <Text fontSize="16px">
              Conte com nosso canal de Suporte Técnico para resolver rapidamente
              os problemas técnicos do seu produto. Oferecemos soluções claras e
              passo a passos simples para orientar nas configurações
              necessárias.
            </Text>
          </Stack>
        )}
        {step.isNaoEncontrado && (
          <Stack>
            <Heading fontSize="20px" fontWeight="600">
              Não encontrou o número de série?
            </Heading>
            <Text fontSize="16px">
              Não se preocupe, tente continuar sem o número de série ou clique
              para abrir um chamado que nossa equipe irá lhe ajudar.
            </Text>
          </Stack>
        )}
        {!step.isBuscarNumeroSerie && !step.isNaoEncontrado && (
          <Stack>
            <Heading fontSize="20px" fontWeight="600">
              Suporte ao cliente
            </Heading>
            {(step.isFinalizarAtendimento ||
              step.isAvaliacaoEnviada ||
              step.isAvaliacao) && (
              <Button
                as="a"
                color="#9246ce"
                href={ROUTER_PATHS.PORTAL.TECHNICAL_SUPPORT}
                paddingInlineStart={0}
                bg="#f7f2fa"
              >
                <Icon as={FaPlus} mx={1} fontSize="20px" />
                Novo atendimento
              </Button>
            )}
          </Stack>
        )}
      </HStack>
    );
  };

  const content = () => {
    return (
      <Stack p={3}>
        {step.isBuscarNumeroSerie && (
          <SearchSeriesNumber
            onChangeObj={item => {
              item && item.numero_serie
                ? setMessage(item.numero_serie.toString())
                : setMessage('');
            }}
          />
        )}
        {step.isOpcoesSuporteTecnico && (
          <>
            {response?.current_messages.map(msg => (
              <AnswerOptions
                key={msg.text}
                pergunta={msg.text}
                opcoes={msg.buttons}
                changeSelecao={nome => {
                  setMessage(nome);
                }}
              />
            ))}
            {loading && (
              <Stack>
                <Skeleton width="200px" height="20px" mb={4} />
                <SkeletonText
                  noOfLines={response?.message.length}
                  spacing="4"
                  height="20px"
                  width="70%"
                />
              </Stack>
            )}
          </>
        )}
        {!step.isAvaliacao &&
          !step.isAvaliacaoEnviada &&
          !step.isFinalizarAtendimento && (
            <StepButtons
              mudarDeTela={stp => {
                // falar com a vic do pq isso n~çao funciona
                if (stp.isNaoEncontrado || stp.isCorrecting) {
                  setMessage('Sem série');
                }
                mudarDeTela(stp);
              }}
              step={step}
              desabilitarProximo={
                !message || message === '' || !uuid || uuid === ''
              }
            />
          )}
        {(step.isAvaliacao || step.isFinalizarAtendimento) && (
          <EvaluateService
            onChange={(avl, texto, passos) => {
              setEvaluate(avl);
              setMessage(texto);
              mudarDeTela(passos, texto, avl);
            }}
            passos={step}
            type={response?.action ? response?.action.event : undefined}
          />
        )}
        {step.isAvaliacaoEnviada && <EvaluateServiceSent />}
      </Stack>
    );
  };

  const sendMessage = () => {
    setLoading(true);
    service(
      message === '' ? 'Sem série' : message,
      uuid,
      response ? response.messages.length : 1,
    )
      .then(res => {
        setMessage('');
        if (res.message.length === 1 && res.message[0].type === 'input') {
          // tratar corrigir numero de serie
          const auxStep = { ...step };
          auxStep.isCorrecting = true;
          auxStep.isBuscarNumeroSerie = false;
          auxStep.isOpcoesSuporteTecnico = true;
          setStep(auxStep);
          // adiciona mensagem nova
          res.current_messages.push({
            text: 'Para informar um novo número de série clique em Voltar ou continue sem número de série.',
            client: false,
            buttons: [],
            date: '',
          });
        }
        setResponse(res);
        if (isAtendimentoFinalizado(res)) {
          const auxStep = { ...step };
          auxStep.isOpcoesSuporteTecnico = false;
          auxStep.isAvaliacao = true;
          setStep(auxStep);
        }
      })
      .catch(e => {
        history.push(ROUTER_PATHS.PORTAL.HOME);
        history.push(ROUTER_PATHS.PORTAL.TECHNICAL_SUPPORT);
        toast({
          title: 'Erro',
          description: e.response.data.detail,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => setLoading(false));
  };

  const sendRating = (avl: number, texto: string) => {
    evaluateService(avl, uuid, texto)
      .then(res => {
        const auxStep = {
          ...step,
          isAvaliacao: false,
          isAvaliacaoEnviada: true,
          isFinalizarAtendimento: false,
        };
        setStep(auxStep);
      })
      .catch(e => {
        history.push(ROUTER_PATHS.PORTAL.HOME);
        history.push(ROUTER_PATHS.PORTAL.TECHNICAL_SUPPORT);
        toast({
          title: 'Erro',
          description: e.response.data.detail,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      });
  };

  const isAtendimentoFinalizado = (res: SelfService) => {
    return !res?.message.length && !!res?.action && 'name' in res?.action;
  };

  const mudarDeTela = (stepObj: NextStep, msg = '', avl?: number) => {
    setResponse(undefined);
    setStep(stepObj);
    if (stepObj.isOpcoesSuporteTecnico || stepObj.isBuscarNumeroSerie) {
      sendMessage();
    }
    if (stepObj.isAvaliacao && !stepObj.isFinalizarAtendimento) {
      if (avl !== undefined) sendRating(avl, msg);
    }
  };

  return (
    <Box px="8" py="4">
      <HStack>
        <Heading>Suporte Técnico Automático</Heading>
        <Text fontSize="20px">- Positivo As A Service</Text>
      </HStack>
      <Flex
        flexDir="row"
        flexWrap="wrap"
        w="100%"
        py={9}
        gridGap="12"
        style={{
          position: 'relative',
        }}
        fontSize="14px"
      >
        <Card header={header()} content={content()} width="70%" bg="#f7f2fa" />
      </Flex>
    </Box>
  );
};
